import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary, LabelStrong } from '@liveauctioneers/hammer-ui-core/text';
import { ReactNode } from 'react';

import { longFormatDate } from '@/utils/dates';

import { PillAlign, PillColor, renderMessageContainer, renderPillMessage } from './renderPillMessage';

const PILL_GREEN_FULL_CLASSNAME = 'block bg-background-success-light p-sm';
const COLUMN_MESSAGE_CLASSNAME = 'flex flex-col items-start justify-center px-sm py-xs';

export type MessageProps = {
    bidderId: number;
    created: number;
    hideMessage: boolean;
    intlValues: {
        amount: ReactNode;
        converted: ReactNode;
        limit: ReactNode;
        lotNumber: string;
        paddleNumber: string;
        serverCreatedTime?: string;
    };
    limit: number;
    messageId: string;
    myBid: boolean;
    nonLaBidder: boolean;
    overlay?: boolean;
    text: ReactNode;
};

// consoleMessages.ts only sets `messageId` on the message object for LIVE_BID_ACCEPTED (via acceptedMessageBuilder),
// e.g. to pick which of several "bid accepted" phrasings applies.
// Every other action type never sets it, so those message types must supply their own id
// — that's what renderMessageWithLiteralId is for below.
const renderMessageWithPayloadId = (
    props: MessageProps,
    color: PillColor,
    options: { align?: PillAlign; textColor?: 'critical' | 'primary' | 'secondary' } = {}
) =>
    renderPillMessage(
        props,
        { ...options, color },
        <FormattedMessage
            id={props.messageId}
            values={props.intlValues}
        />
    );

const renderMessageWithLiteralId = (props: MessageProps, id: string, color: PillColor = 'blue') =>
    renderPillMessage(
        props,
        { color },
        <FormattedMessage
            id={id}
            values={props.intlValues}
        />
    );

const buildMessageWithLiteralId = (id: string) => (props: MessageProps) => renderMessageWithLiteralId(props, id);

const fixedPillMessages = {
    askChanged: buildMessageWithLiteralId('bidding_console.console.messages.ask_price_changed_to'),
    exceeded: buildMessageWithLiteralId('bidding_console.console.messages.bid_limit_exceeded'),
    opened: buildMessageWithLiteralId('bidding_console.console.messages.lot_opened_asking'),
    passed: buildMessageWithLiteralId('bidding_console.console.messages.lot_number_passed'),
    reopened: buildMessageWithLiteralId('bidding_console.console.messages.lot_number_reopened'),
    skipped: buildMessageWithLiteralId('bidding_console.console.messages.lot_number_skipped'),
    unsold: buildMessageWithLiteralId('bidding_console.console.messages.lot_number_unsold'),
};

const renderFullMessage = ({ created }: MessageProps, content: ReactNode) =>
    renderMessageContainer(
        created,
        'left',
        <div className={PILL_GREEN_FULL_CLASSNAME}>
            <LabelStrong>{content}</LabelStrong>
        </div>
    );

const renderColumnMessage = ({ created, intlValues: { serverCreatedTime } }: MessageProps, labelId: string) =>
    renderMessageContainer(
        created,
        'left',
        <div className={COLUMN_MESSAGE_CLASSNAME}>
            <LabelStrong className="mb-xs inline-block">
                <FormattedMessage id={labelId} />
            </LabelStrong>
            {Boolean(serverCreatedTime) && (
                <BodySecondary color="secondary">{longFormatDate(new Date(serverCreatedTime))}</BodySecondary>
            )}
        </div>
    );

const outBidMessage = (props: MessageProps) => renderMessageWithPayloadId(props, 'grey');

const acceptedMessage = (props: MessageProps) => renderMessageWithPayloadId(props, 'grey', { textColor: 'secondary' });

const approvedMessage = (props: MessageProps) =>
    renderFullMessage(
        props,
        props.limit ? (
            <FormattedMessage
                id="bidding_console.console.messages.limited_approval"
                values={props.intlValues}
            />
        ) : (
            <FormattedMessage id="bidding_console.console.messages.registration_approved" />
        )
    );

const endedMessage = (props: MessageProps) => renderPillMessage(props, { color: 'blue' }, props.text);

const MISSIVE_INTL_IDS: Record<string, string> = {
    'Fair Warning... This lot is about to close': 'bidding_console.console.missives.fair_warning_lot_about_to_close',
    'GOING ONCE...GOING TWICE...': 'bidding_console.console.missives.going_once_going_twice',
    'THIRD AND FINAL WARNING...': 'bidding_console.console.missives.third_and_final_warning',
};

const missiveMessage = (props: MessageProps) => {
    const { text } = props;
    const intlId = typeof text === 'string' ? MISSIVE_INTL_IDS[text] : undefined;
    const missiveText = intlId ? <FormattedMessage id={intlId} /> : text;

    return renderPillMessage(props, { color: 'red', textColor: 'critical' }, missiveText);
};

const myBidMessage = (props: MessageProps) =>
    renderMessageWithPayloadId(props, 'green', { align: 'right', textColor: 'secondary' });

const pausedMessage = (props: MessageProps) => renderColumnMessage(props, 'bidding_console.console.messages.paused');

const reserveMessage = (props: MessageProps) => renderMessageWithPayloadId(props, 'blue');

const resumedMessage = (props: MessageProps) => renderColumnMessage(props, 'bidding_console.console.messages.resumed');

const retractedMessage = (props: MessageProps) =>
    renderMessageWithLiteralId(
        props,
        props.myBid
            ? 'bidding_console.console.messages.my_bid_retracted'
            : 'bidding_console.console.messages.last_bid_retracted'
    );

const soldMessage = (props: MessageProps) => {
    if (props.myBid) {
        return renderFullMessage(
            props,
            <FormattedMessage
                id="bidding_console.console.messages.you_ve_won_lot"
                values={props.intlValues}
            />
        );
    }

    return renderPillMessage(
        props,
        { color: 'blue' },
        props.bidderId === 0 ? (
            <FormattedMessage
                id="bidding_console.console.messages.lot_sold_competing_bidder"
                values={props.intlValues}
            />
        ) : (
            <FormattedMessage
                id="bidding_console.console.messages.lot_sold_liveauctioneers_bidder"
                values={props.intlValues}
            />
        )
    );
};

export default {
    'absentee-outbid': outBidMessage,
    accepted: acceptedMessage,
    accepted2: acceptedMessage,
    approved: approvedMessage,
    ended: endedMessage,
    missive: missiveMessage,
    mybid: myBidMessage,
    outbid: outBidMessage,
    'outbid-absentee': outBidMessage,
    paused: pausedMessage,
    reserve: reserveMessage,
    resumed: resumedMessage,
    retracted: retractedMessage,
    sold: soldMessage,
    ...fixedPillMessages,
};
